import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { resolveConflict } from '../../../services/sandbox.js';

/** "Confirm this version" in Compare (CT-5718): one request per double click, a toast, then `onResolved`. */
export function useResolveConflict({ entityType, entityId, claimId, onResolved } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [resolving, setResolving] = useState(false);
  const submittingRef = useRef(false);

  const resolve = useCallback(
    async (choice) => {
      if (submittingRef.current || !choice) return false;
      submittingRef.current = true;
      setResolving(true);
      try {
        await resolveConflict({ entityType, entityId, claimId, choice });
        toast.success({
          title: t('knowledge.sandbox.toast.resolvedTitle'),
          message: t(choice === 'unsure' ? 'knowledge.sandbox.toast.resolvedUnsure' : 'knowledge.sandbox.toast.resolvedMessage'),
        });
        onResolved?.();
        return true;
      } catch (err) {
        toast.error({ title: t('knowledge.sandbox.toast.errorTitle'), message: err?.message ?? t('knowledge.sandbox.toast.errorMessage') });
        return false;
      } finally {
        submittingRef.current = false;
        setResolving(false);
      }
    },
    [entityType, entityId, claimId, onResolved, toast, t],
  );

  return useMemo(() => ({ resolve, resolving }), [resolve, resolving]);
}

export default useResolveConflict;
